import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Typography, Tag, Tooltip, Input, Space, Modal, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../redux/message.reducer';

const { Title } = Typography;

const DEFAULT_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 400;

const formatDate = (value) => {
  if (!value) return '—';
  const d = dayjs(value);
  return d.isValid() ? d.format('MMM D, YYYY h:mm A') : '—';
};

export default function ErrorLogs() {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchInput, setSearchInput] = useState('');
  const [searchApplied, setSearchApplied] = useState('');
  const [detailModal, setDetailModal] = useState({ open: false, title: '', content: '' });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchErrors = useCallback(async (page, limit, search) => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search && search.trim()) params.search = search.trim();
      const res = await axios.get('/errors', { params });
      setErrors(res.data?.data ?? []);
      setPagination((prev) => ({
        ...prev,
        current: res.data?.page ?? page,
        pageSize: res.data?.limit ?? limit,
        total: res.data?.total ?? 0,
      }));
    } catch (err) {
      dispatch(setMessage({ message: err.response?.data?.error || 'Failed to load errors', type: 'error' }));
      setErrors([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchErrors(pagination.current, pagination.pageSize, searchApplied);
  }, [pagination.current, pagination.pageSize, searchApplied, fetchErrors]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchApplied(searchInput.trim());
      setPagination((prev) => ({ ...prev, current: 1 }));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleDelete = () => {
    if (selectedRowKeys.length === 0) return;
    Modal.confirm({
      title: `Delete ${selectedRowKeys.length} error log${selectedRowKeys.length > 1 ? 's' : ''}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        setDeleteLoading(true);
        try {
          const res = await axios.delete('/errors', { data: { ids: selectedRowKeys } });
          dispatch(setMessage({ message: `${res.data?.deleted ?? 0} error(s) deleted`, type: 'success' }));
          setSelectedRowKeys([]);
          fetchErrors(pagination.current, pagination.pageSize, searchApplied);
        } catch (err) {
          dispatch(setMessage({ message: err.response?.data?.error || 'Delete failed', type: 'error' }));
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  const onTableChange = (pag) => {
    setPagination((prev) => ({
      ...prev,
      current: pag.current ?? prev.current,
      pageSize: pag.pageSize ?? prev.pageSize,
    }));
  };

  const columns = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: formatDate,
    },
    {
      title: 'Method',
      dataIndex: 'method',
      key: 'method',
      width: 80,
      render: (val) => <Tag>{val || '—'}</Tag>,
    },
    {
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
      render: (val) => (
        <Tooltip title={val}>
          <span style={{ color: '#cf1322' }}>{val || '—'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      width: 150,
      render: (val) => val?.name || val?.email || '—',
    },
    {
      title: 'Stack',
      dataIndex: 'stack',
      key: 'stack',
      width: 120,
      render: (val) =>
        val ? (
          <Tag
            color="red"
            style={{ cursor: 'pointer' }}
            onClick={() => setDetailModal({ open: true, title: 'Stack Trace', content: val })}
          >
            View stack
          </Tag>
        ) : '—',
    },
    {
      title: 'Body',
      dataIndex: 'reqBody',
      key: 'reqBody',
      width: 100,
      render: (val) =>
        val && Object.keys(val).length > 0 ? (
          <Tag
            color="blue"
            style={{ cursor: 'pointer' }}
            onClick={() => setDetailModal({ open: true, title: 'Request Body', content: JSON.stringify(val, null, 2) })}
          >
            View body
          </Tag>
        ) : '—',
    },
  ];

  return (
    <div>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <Title level={3} style={{ margin: 0 }}>Error Logs</Title>
          {selectedRowKeys.length > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={handleDelete}
              loading={deleteLoading}
              className="app-btn-delete"
            >
              Delete {selectedRowKeys.length} selected
            </Button>
          )}
        </div>
        <Input
          placeholder="Search by route, method, or message"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          allowClear
          style={{ maxWidth: 480 }}
        />
      </Space>
      <Card style={{ marginTop: 16 }}>
        <Table
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
          }}
          columns={columns}
          dataSource={errors.map((row) => ({ ...row, key: row._id }))}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} errors`,
            pageSizeOptions: ['10', '20', '50'],
          }}
          onChange={onTableChange}
          size="middle"
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={detailModal.title}
        open={detailModal.open}
        onCancel={() => setDetailModal({ open: false, title: '', content: '' })}
        footer={null}
        width={700}
        destroyOnClose
      >
        <pre style={{ maxHeight: 500, overflow: 'auto', margin: 0, fontSize: 12, background: '#f5f5f5', padding: 16, borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {detailModal.content}
        </pre>
      </Modal>
    </div>
  );
}
